import React, { FC } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import { Element, scale, notchTop, topOffsetForPosition, scaleFactor, notchBottom, width, height } from '@components/frame';
import { isPhone } from '@helpers';
import ColorsWrapper from '@styles/colors';
import TouchableElement from '@components/common/touchableElement';
import { font } from '@styles/fonts';
import RotateIcon from '@assets/icons/rotate.svg';
import DiscardChangesPopup from '@v2/components/imageHandling/components/Cropper/components/DiscardChangesPopup';
import CropperTabletMobile, { CropperTabletMobileProps } from '@v2/components/imageHandling/components/Cropper/components/CropperTabletMobile';
import { CropperTabletProps } from '@v2/components/imageHandling/components/Cropper/components/CropperTablet';
import { isAndroid } from 'react-device-detect';
import { useManageCropperMobileTablet } from '@v2/components/imageHandling/components/Cropper/hooks/useManageCropperMobileTablet';
import ChooseCropModeModal from '@v2/components/imageHandling/components/Cropper/components/ChooseCropModeModal';
import { useManageChooseCropModeModal } from '@v2/components/imageHandling/components/Cropper/hooks/useManageChooseCropModeModal';

interface CropperMobileProps extends CropperTabletProps {}

const CropperMobile: FC<CropperMobileProps> = (props) => {
  const { triggerShow, C, onSave, image, isNewImage = false } = props;

  const {
    angle,
    modeRef,
    onCancelButtonPress,
    onRotateImage,
    onSaveButtonPress,
    showDiscardChangesPopup,
    triggerDiscardChangesPopup,
    cropRef,
    showChooseCropModeModalOpen,
    setShowChooseCropModeModalOpen,
    pendingCropData,
    setPendingCropData,
    processCropData,
  } = useManageCropperMobileTablet({ image, onSave, isNewImage });

  const cropperProps: CropperTabletMobileProps = {
    image,
    cropRef,
    angle,
    onSave,
    triggerShow,
    C,
    modeRef,
    triggerDiscardChangesPopup,
    setShowChooseCropModeModalOpen,
    setPendingCropData,
    isNewImage,
  };

  const { config, onCloseShowChooseCropModeModalOpen } = useManageChooseCropModeModal({
    closeCrop: triggerShow,
    image,
    pendingCropData,
    processCropData,
    setPendingCropData,
    setShowChooseCropModeModalOpen,
  });

  return [
    <DiscardChangesPopup {...{ showDiscardChangesPopup, triggerDiscardChangesPopup, cancelCrop: triggerShow, C }} key="DiscardChangesPopup" />,
    <ChooseCropModeModal
      key="ChooseCropModeModal"
      isVisible={showChooseCropModeModalOpen}
      onClose={onCloseShowChooseCropModeModalOpen}
      C={C}
      config={config}
      containerStyle={{
        bottom: scale(100),
        right: scale(12),
        alignItems: 'flex-end',
      }}
    />,
    <View
      style={{
        position: 'absolute',
        zIndex: 11000,
        width: width,
        height: height,
        backgroundColor: C('Label / Light / AlwaysDark Primary'),
      }}
      key="CropperBox"
    >
      <Element
        {...{
          config: {
            backgroundColor: C('Label / Light / AlwaysDark Primary'),
            position: {
              top: isAndroid && isPhone ? -topOffsetForPosition / scaleFactor : 0,
              left: 0,
              right: 0,
            },
            height: notchTop + 51.5,
            borderBottomColor: 'rgba(255, 255, 255, 0.2)',
            borderBottomWidth: 1,
          },
        }}
      >
        <Element
          {...{
            config: {
              position: {
                top: 20.5 + notchTop,
                left: 0,
                right: 0,
                bottom: 0,
              },
              justifuContent: 'center',
            },
          }}
        >
          <TouchableElement>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle, { width: '100%' }]}>
                <Text
                  style={[
                    {
                      color: C(pressHoverColor('Label / Light / AlwaysLight Primary')),
                      textTransform: 'uppercase',
                      width: '100%',
                      textAlign: 'center',
                    },
                    font.Headline,
                  ]}
                >
                  {i18n.t('ADJUST')}
                </Text>
              </View>
            )}
          </TouchableElement>
        </Element>
      </Element>
      <Element
        {...{
          config: {
            position: {
              bottom: 0,
              right: 0,
              left: 0,
            },
            height: 42,
            backgroundColor: C('Label / Light / AlwaysDark Primary'),
          },
        }}
      >
        <Element
          {...{
            config: {
              position: {
                top: 0,
                left: 18,
              },
              zIndex: 100,
            },
          }}
        >
          <TouchableElement onPress={onCancelButtonPress}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                <Text style={[{ color: C(pressHoverColor('Label / Light / AlwaysLight Primary')) }, font.Headline]}>{i18n.t('Cancel')}</Text>
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element
          {...{
            config: {
              position: {
                top: 0,
                left: '46.27%',
                right: '46.27%',
                justifuContent: 'center',
                alignItems: 'center',
                alignContent: 'center',
              },
              zIndex: 100,
            },
          }}
        >
          <TouchableElement onPress={onRotateImage}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                <RotateIcon width={scale(17)} height={scale(18)} />
              </View>
            )}
          </TouchableElement>
        </Element>
        <Element
          {...{
            config: {
              position: {
                top: 0,
                right: 18,
              },
              zIndex: 100,
            },
          }}
        >
          <TouchableElement onPress={onSaveButtonPress}>
            {({ boxStyle, pressHoverColor }) => (
              <View style={[boxStyle]}>
                <Text style={[{ color: C(pressHoverColor('Label / Dark / Favorite')) }, font.Headline]}>{i18n.t('Done')}</Text>
              </View>
            )}
          </TouchableElement>
        </Element>
      </Element>
      <CropperTabletMobile {...cropperProps} />
    </View>,
  ];
};

export default ColorsWrapper(CropperMobile);
